<template>
    <div
        v-if="suggestions.length"
        class="ask-noticed"
        data-testid="ask-noticed"
        role="group"
        :aria-label="label"
    >
        <!-- Whose voice this is. Floating clear of the panel, nothing else
             says so — and the caller says what the pills are about: what the
             answer named, or the page the box opened over. The same words
             name the group for a screen reader, so the span itself is
             decoration rather than a second reading of them. -->
        <span class="ask-noticed__label" aria-hidden="true">{{ label }}</span>

        <!-- One pill per card and nothing else: no dismiss, no snooze, no
             apply. Ignoring one costs nothing — the same card is still on the
             landing page — so there is nothing here to manage, only something
             to ask about. -->
        <button
            v-for="suggestion in suggestions"
            :key="suggestion.identity"
            type="button"
            class="ask-noticed__pill"
            :data-testid="`ask-noticed-${suggestion.identity}`"
            @click="emit('open', suggestion.identity)"
        >
            <i class="pi pi-sparkles" aria-hidden="true" />
            <!-- The short form. `statement` is the fallback for a rule nobody
                 has phrased short yet, and for a record stored before the
                 backend had one. -->
            <span>{{ suggestion.short || suggestion.statement }}</span>
        </button>
    </div>
</template>

<script setup lang="ts">
import type { Suggestion } from "@/lib/openapi/gen/types.gen";

/**
 * What gotoko has noticed, offered as pills floating above the box.
 *
 * The landing page's suggestions, in the landing page's words, in a smaller
 * form: over the box when it opens on an entity's page, and over it again
 * under the latest answer. One component for both moments, so a reader who
 * has learned the page has learned the box.
 *
 * **Outside the panel, not in it.** The pills are gotoko speaking unprompted
 * — an aside — and the panel is the exchange: what you asked, what it said.
 * Inside the panel they read as part of the answer, which is the one thing
 * they are not. Floating just above it they are visibly a different voice,
 * and they can appear and vanish without moving a line of the conversation.
 * Two earlier shapes were tried on the way here: a labelled list of bulleted
 * lines inside the panel, which read as a queue of work; and pills sitting
 * directly under the answer, which read as its continuation.
 *
 * Each pill hugs its own sentence rather than stretching the width of the
 * strip, so two of them read as two offers rather than as rows of a table.
 * The sentence is the page's own short form: the same finding must not be
 * worded two ways on two surfaces.
 *
 * Told apart from the reference chips inside the panel — which look similar
 * and do something else entirely, navigate rather than ask — by being outside
 * it, by the sparkle the app uses everywhere for gotoko's own suggestions, and
 * by the small label naming the speaker. The label is an eyebrow rather than
 * a heading: detached from the panel, the strip needs a word saying whose
 * voice it is, not a title over a list.
 */
defineProps<{ suggestions: Suggestion[]; label: string }>();

const emit = defineEmits<{ open: [identity: string] }>();
</script>

<style scoped>
/* One row that wraps, label first. The strip is mostly air between the pills
   and the page behind it must stay clickable, so the strip itself takes no
   pointer events and each pill takes them back. */
.ask-noticed {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.375rem 0.5rem;
    padding: 0 0.25rem;
    pointer-events: none;
}

/* An eyebrow, deliberately: floating clear of the panel, the pills need a
   word saying whose voice this is, and a small spaced-out tag reads as that
   where a sentence would read as a heading over a list of work. */
.ask-noticed__label {
    color: var(--p-text-muted-color);
    font-size: 0.65rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    white-space: nowrap;
    opacity: 0.85;
}

/* Each pill is as wide as its own sentence: a full-width version reads as a
   list to work through, which is the opposite of what it is for. Its own
   ground and a faint shadow because it floats over the page now rather than
   sitting on the panel — the same treatment the panel has, scaled down, and
   on the panel's own tokens for ground and border. Those are the semantic
   ones that flip with dark mode; the raw `--p-surface-*` scale does not, so a
   pill drawn from it sits near-black with a light-grey ring the moment the
   theme changes. No mode colour, like everything else in the box: it answers
   across Design, Build and Run at once. */
.ask-noticed__pill {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    max-width: 100%;
    padding: 0.3rem 0.7rem 0.3rem 0.55rem;
    border: 1px solid var(--p-overlay-popover-border-color);
    border-radius: 999px;
    background: var(--p-overlay-popover-background);
    box-shadow: 0 0.0625rem 0.1875rem rgb(0 0 0 / 8%);
    color: var(--p-text-muted-color);
    font: inherit;
    font-size: 0.8rem;
    line-height: 1.3;
    text-align: left;
    cursor: pointer;
    pointer-events: auto;
    transition:
        color 0.15s,
        border-color 0.15s,
        background-color 0.15s;
}

/* Colour and border only, as the reference chips do: a background swap here
   would come from the raw surface scale, which is light in both themes, and
   a dark-mode pill would flash white on hover. */
.ask-noticed__pill:hover,
.ask-noticed__pill:focus-visible {
    border-color: var(--p-primary-color);
    color: var(--p-primary-color);
}

.ask-noticed__pill:focus-visible {
    outline: 1px solid var(--p-primary-color);
    outline-offset: 1px;
}

/* The one mark of gotoko's own voice, in the colour the app gives it
   everywhere, so a pill is never mistaken for a reference chip at a glance. */
.ask-noticed__pill i {
    flex: none;
    font-size: 0.7rem;
    color: var(--p-primary-color);
}
</style>
